<template>

  <v-data-table-server
    v-model:items-per-page="pagination.limit"
    v-model:page="pagination.page"
    v-model:sort-by="pagination.sort"
    :headers="heads"
    v-model:items-length="count"
    :items="items"
    :loading="loading"
    density="compact"
    class="border rounded"
    no-data-text="Nessun elemento inserito"
  >

    <template v-for="(_, name) in ($slots as {})" #[name]="slotData">
      <slot :name="name" v-bind="slotData || {}" />
    </template>

    <template #item.view="{item}">
      <div class="d-flex w-100 justify-center">
        <v-btn variant="text" :icon="viewIcon" color="primary" @click="view(item)"></v-btn>
      </div>
    </template>
    <template #item.edit="{item}">
      <div class="d-flex w-100 justify-center">
        <v-btn  variant="text" icon="mdi-pencil" color="primary" @click="edit(item)"></v-btn>
      </div>
    </template>
    <template #item.delete="{item}">
      <v-btn variant="text" icon="mdi-close" color="error" @click="requestDelete(item)"></v-btn>
    </template>

  </v-data-table-server>
</template>

<script lang="ts">
import {defineComponent, inject,  onBeforeUnmount, onMounted, type PropType, ref, watch, type WatchStopHandle, computed} from "vue";
import api from "@/api";
import {useRouter} from "vue-router";
import log from "@/log";
import utils from "@/utils";
import {Messages} from "@/plugins/messages";
import type {DbDoc, Pagination, DataTableError, SortItem} from "@/models";
import type {Auth} from "@/plugins/auth";


export default defineComponent({
  props : {
    collection : {
      type : String,
      required : true
    },
    listCollection : {
      type : String,
      default : null
    },
    headers : {
      type : Array as PropType<Array<any>>,
      required : true
    },
    query : {
      type :Object,
      default: {}
    },
    queryFilter : {
      type :Object,
      default: {}
    },
    deletable : {
      type : Boolean,
      default: true
    },
    editable : {
      type : Boolean,
      default: true
    },
    viewable : {
      type : Boolean,
      default: false
    },
    viewIcon : {
      type : String,
      default : "mdi-eye"
    },
    viewSubPath : {
      type : String,
      default : "/view"
    },
    createBtn : {
      type : Boolean,
      default: true
    },
    basePath : String,
    createPath : String,
    defaultLimit : {
      type:Number,
      default : 10
    },
    defaultSort :Object as PropType<SortItem>,
    sessionKey:String,
  },
  setup(props, ctx) {
    const auth = inject<Auth>('auth') as Auth
    const messages = inject<Messages>('messages') as Messages

    const loading = ref(false)
    const items = ref<Array<DbDoc>>([])
    const count = ref<number>(0)
    const router = useRouter()


    const pagination = ref<Pagination>({
      page : 1,
      sort : [],
      limit : 10,
      count : 0
    })





    const loadItems = async () => {
      loading.value = true

      const sort = pagination.value.sort.length ? pagination.value.sort.map(o => `${o.order === 'asc' ? '+':'-'}${o.key}`).join(",") : null
      const res = await api.list(props.listCollection ?? props.collection, {...props.query, ...props.queryFilter}, pagination.value.limit, pagination.value.page-1, sort)
      items.value = res.data || []
      count.value = res.count || 0;

      if(!res.success) {
        ctx.emit('error', { status: res.status, message: res.message } as DataTableError)
      }

      loading.value = false;
    }

    ctx.expose({loadItems})



    let wh:WatchStopHandle, wh2:WatchStopHandle, wh3:WatchStopHandle;
    onMounted(async ()=>{
      pagination.value.limit = props.defaultLimit
      if(props.defaultSort) {
        pagination.value.sort = [props.defaultSort]
      }
      let c = sessionStorage.getItem(`${props.collection}Count`)
      if(c) {
        count.value = utils.toInt(c, 0)
      }

      /*
      let q = sessionStorage.getItem(`${props.collection}Query`)
      if(q) {
        try {
          ctx.emit('update:query', JSON.parse(q))
        } catch (e) {
          log.d(e)
        }
      }*/

      let p = sessionStorage.getItem(`${props.sessionKey ?? props.collection}Pagination`)
      if(p) {
        try {
          pagination.value = JSON.parse(p)
          //console.log('pagination.value restore', p, pagination.value)
        } catch (e) {
          log.d(e)
        }

      }

      let tm:any;
      wh = watch(()=>props.query, async ()=>{
        //sessionStorage.setItem(`${props.collection}Query`, JSON.stringify(props.query))
        clearTimeout(tm);
        tm = setTimeout(loadItems, 250);
      },{deep:true})
      wh2 = watch(pagination, async ()=>{
        //console.log("setitem", JSON.stringify(pagination.value))
        sessionStorage.setItem(`${props.collection}Pagination`, JSON.stringify(pagination.value))
        await loadItems()
      },{deep:true})

      wh3 = watch(count, async ()=>{
        //console.log("set count", count.value)
        sessionStorage.setItem(`${props.collection}Count`, JSON.stringify(count.value))
      },{deep:true})

      await loadItems();
    })
    onBeforeUnmount(()=>{
      wh(); wh2(); wh3();
    })
    return {
      loading,
      items,
      count,
      pagination,
      loadItems,
      view( item:any ) {
        const basePath = props.basePath || props.collection
        router.push(`/${basePath}/${item._id}${props.viewSubPath}`)
      },
      edit( item:any ) {
        if (!item && props.createPath) {
          router.push(props.createPath)
          return
        }

        const basePath = props.basePath || props.collection

        router.push(item ? `/${basePath}/${item._id}` : `/${basePath}/new`)
      },
      requestDelete( item:DbDoc ) {
        messages.requestConfirm("Richiesta conferma", "Vuoi davvero eliminare questo record?", false, async ok => {
          if(!ok) return;
          loading.value = true
          const res = await api.del(props.collection, item._id as string)
          if(!res.success) {
            loading.value = false;
            if(res.status === 401) {
              messages.error("Non hai i permessi per effettuare questa richiesta");
              auth.logout()
              return
            }
            messages.error(res.message || "c'è stato un errore, riprova più tardi")
            return;
          }
          messages.success("record eliminato")
          await loadItems()
        })
      },
      heads : computed<Array<any>>(():Array<any>=>{

        let heads = [...props.headers]

        if(props.viewable) {
          heads.push({ title: '', align: 'center', key: 'view', width : 60, sortable: false })
        }
        if(props.editable) {
          heads.push({ title: '', align: 'center', key: 'edit', width : 60, sortable: false })
        }
        if(props.deletable) {
          heads.push({ title: '', align: 'center', key: 'delete', width : 60, sortable: false })
        }
        return heads
      }),
    }
  }
})
</script>

<style scoped>

</style>
